/**
 * Printable RMA result summaries — one summary per page, for one RMA or a
 * whole grid selection.
 *
 * Serves two routes: the single result-summary page (`/rma/:rma_id/
 * resultsummary`, the quick-print target from the RMA grid and detail menu)
 * and the batch page (`/rmas/print?ids=1,2,3`, the grid's Print Selected
 * action). Each RMA renders through ResultSummaryView with a page break
 * after it, so the browser's print dialog produces one sheet per RMA.
 *
 * Contains:
 * - ReturnPrintView — routed printable page (default export)
 */
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { fetchRma } from '@sophocles/entities/Rma';
import ResultSummaryView from '@sophocles/views/returns/ResultSummaryView';

import '@sophocles/styles/print-order.css';

function ReturnPrintView() {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { rma_id: rmaIdParam } = useParams();
  const [searchParams] = useSearchParams();

  const ids = useMemo(() => {
    if (rmaIdParam) return [rmaIdParam];
    return (searchParams.get('ids') || '')
      .split(',')
      .map(id => id.trim())
      .filter(Boolean);
  }, [rmaIdParam, searchParams]);

  const [rmas, setRmas] = useState(null);
  const [fetchError, setFetchError] = useState(null);

  useEffect(() => {
    if (!ids.length) {
      setFetchError(t('rma.printNothingSelected', 'No RMAs to print.'));
      return undefined;
    }
    let cancelled = false;
    Promise.all(ids.map(id => dispatch(fetchRma(id))))
      .then(results => {
        if (!cancelled) setRmas(results);
      })
      .catch(() => {
        if (!cancelled) setFetchError(t('rma.fetchError', 'Unable to load RMA. Please try again.'));
      });
    return () => {
      cancelled = true;
    };
  }, [dispatch, ids, t]);

  if (fetchError) {
    return (
      <div className="print-order-page">
        <Typography color="error">{fetchError}</Typography>
      </div>
    );
  }

  if (!rmas) {
    return (
      <div className="print-order-page">
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      </div>
    );
  }

  // Same bare shell as the order print page: no app chrome, the print-order
  // CSS handles page width on screen and strips the button row in print.
  return (
    <div className="print-order-page">
      <div className="no-print" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <Button variant="outlined" onClick={() => navigate(-1)}>
          {t('actions.back', 'Back')}
        </Button>
        <Button variant="contained" onClick={() => window.print()}>
          {t('actions.print', 'Print')}
        </Button>
      </div>
      {rmas.map(rma => (
        <Box key={rma.get('id')} sx={{ breakAfter: 'page', mb: 3 }}>
          <ResultSummaryView rma={rma} />
        </Box>
      ))}
    </div>
  );
}

export default ReturnPrintView;
